import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';

import withLeftSideBar from '../hocs/withLeftSideBar';
import IconButton from '../components/IconButton';
import Spinner from '../components/Spinner';
import {
  MainHeader,
  MainContentTopBar,
  ContainerWrapper,
} from '../components/basicComponents';
import TranslationsTable from '../components/TranslationsTable';
import i18n from '../i18n';
import { getInterpreterTranslations } from '../services/translationsService';
import { showError } from '../services/toastService';
import { formatDate } from '../utils/dateUtils';

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  flex-direction: column;
`;

const mapTranslation = (t) => ({
  id: t._id,
  customerID: t.orderNumber,
  fromLanguage: t.sourceLanguageNameEn,
  toLanguage: t.targetLanguageNameEn,
  dueDate: formatDate(t.dueDate),
  dueDateRaw: t.dueDate ? new Date(t.dueDate).getTime() : 0,
});

const InterpreterTranslations = ({ toggleSideBar, sidebarDocked, ...rest }) => {
  const { colors, layout } = useContext(ThemeContext);
  const { sessionExpired } = useContext(FunctionsContext);
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [translations, setTranslations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const data = await getInterpreterTranslations();
        if (!cancelled) {
          const list = Array.isArray(data) ? data : (data.translations || []);
          setTranslations(list.map(mapTranslation));
          document.dispatchEvent(new CustomEvent('updateTranslationsCount', {
            detail: { count: list.length }
          }));
        }
      } catch (e) {
        if (e.message === 'Session expired') {
          sessionExpired();
        } else {
          showError(i18n('requestErrorMessage'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(() => [
    { key: 'customerID', label: i18n('translation_customer_id'), render: (val) => `# ${val}` },
    { key: 'fromLanguage', label: i18n('translation_from') },
    { key: 'toLanguage', label: i18n('translation_to') },
    { key: 'dueDate', label: i18n('translation_due_date'), sortKey: 'dueDateRaw' },
  ], []);

  const handleOpen = (translation) => {
    history.push(`/translations/${translation.id}`);
  };

  return (
    <StyledContainer {...rest}>
      <MainContentTopBar borderBottomColor={colors.webDeviderColor}>
        {sidebarDocked ? null : (
          <IconButton
            icon={'menu'}
            iconLib={'material'}
            iconColor={colors.tulkaMainColor}
            onClick={toggleSideBar}
          />
        )}
        <MainHeader
          sidebarDocked={sidebarDocked}
          padding={layout.padding}
          color={colors.webDarkBlue}
        >
          {i18n('translation_assigned_jobs')}
        </MainHeader>
      </MainContentTopBar>
      <ContainerWrapper>
        <TranslationsTable
          columns={columns}
          data={translations}
          onRowOpen={handleOpen}
        />
      </ContainerWrapper>
      {loading && <Spinner overlay />}
    </StyledContainer>
  );
};

export default withLeftSideBar(InterpreterTranslations);
